/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Box } from '@mui/material';
import { type ReactNode, useState, useEffect, useContext } from 'react';
import type { SidebarComponentProps } from '../../constants/tabs';
import { SearchContext } from '../../contexts/SearchContext';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { isPluginOrGroupAvailable } from '../../utils/isPluginOrGroupAvailable/isPluginOrGroupAvailable';
import { BuilderToggleButtons } from '../BuilderToggleButtons/BuilderToggleButtons';
import { NoResultsWrapper } from '../NoResultsWrapper/NoResultsWrapper';
import { SearchField } from '../SearchField/SearchField';
import { SidebarSectionHeader } from '../SidebarSectionHeader/SidebarSectionHeader';
import { PluginSelectionAdvancedView } from './PluginSelectionAdvancedView/PluginSelectionAdvancedView';
import { PluginSelectionBasicView } from './PluginSelectionBasicView/PluginSelectionBasicView';
import type { FeatureParsed, PluginOrPluginGroupParsed, PluginSelectionMode } from './types';
import { getCapabilitiesForView } from '../../utils/getCapabilitiesForView/getCapabilitiesForView';
import { isCapability } from '../../utils/isCapability/isCapability';
import { isCapabilitySingleChoice } from '../../utils/isCapabilitySingleChoice/isCapabilitySingleChoice';
import { isPlugin } from '../../utils/isPlugin/isPlugin';
import { GTMContext } from '../../contexts/GTMContext';
import { GTM_EVENT_FEATURE_SELECT, GTM_EVENT_GROUP_SELECT, GTM_EVENT_PLUGIN_SELECT, GTM_EVENT_SEARCH } from '../../constants/GTMEventNames';
import { getCoreCapability } from '../../utils/getCoreCapability/getCoreCapability';
import { EditorSettingsContext } from '../../contexts/EditorSettingsContext';

const views: Array<{ type: PluginSelectionMode; name: string }> = [
	{ type: 'basic', name: 'Basic view' },
	{ type: 'advanced', name: 'Advanced view' }
];

export function PluginSelection( {
	activeTab,
	featuresView,
	onFeaturesViewChange,
	onGroupToggle
}: SidebarComponentProps ): ReactNode {
	const {
		editorType,
		editorPlugins,
		capabilities,
		toolbarOptions,
		onEditorPluginsSelect,
		onEditorPluginsDeselect
	} = useContext( EditorSettingsContext );
	const { triggerGTMEvent } = useContext( GTMContext );

	const [ searchQuery, setSearchQuery ] = useState( '' );

	const ViewComponent = featuresView === 'basic' ? PluginSelectionBasicView : PluginSelectionAdvancedView;
	const filteredCapabilities = getCapabilitiesForView( {
		editorType,
		capabilities: featuresView === 'basic' ?
			capabilities.concat( getCoreCapability() ) :
			capabilities,
		editorPlugins,
		searchQuery,
		view: featuresView
	} );

	const hasVisibleCapabilities = filteredCapabilities.some( capability => !capability.isFiltered );

	useEffect( () => {
		if ( searchQuery.length < 2 ) {
			return;
		}

		const visibleCapabilities = filteredCapabilities.filter( c => !c.isFiltered );
		const visibleFeatures = visibleCapabilities.flatMap( c => c.features.filter( f => !f.isFiltered ) );
		const visiblePlugins = visibleFeatures.filter( f => f.plugins.length > 1 ).flatMap( f => f.plugins.filter( p => !p.isFiltered ) );

		triggerGTMEvent( {
			event: GTM_EVENT_SEARCH,
			stage: 'features',
			view: featuresView,
			query: searchQuery,
			results: featuresView === 'basic' ?
				visibleCapabilities.length + visibleFeatures.length :
				visibleCapabilities.length + visibleFeatures.length + visiblePlugins.length
		} );
	}, [ filteredCapabilities, featuresView, searchQuery, triggerGTMEvent ] );

	function handleInputClick( pluginOrGroup: PluginOrPluginGroupParsed ) {
		const groupPlugins = getPluginsToModifyFromGroup( pluginOrGroup );

		let event: string;
		let action: string;

		if ( 'features' in pluginOrGroup ) {
			event = GTM_EVENT_GROUP_SELECT;
		} else if ( 'plugins' in pluginOrGroup ) {
			event = GTM_EVENT_FEATURE_SELECT;
		} else {
			event = GTM_EVENT_PLUGIN_SELECT;
		}

		const isSelectedIndeterminate = 'isSelectedIndeterminate' in pluginOrGroup && pluginOrGroup.isSelectedIndeterminate;

		if ( pluginOrGroup.isSelected ) {
			action = 'unselect';
		} else if ( isSelectedIndeterminate ) {
			action = 'partial';
		} else {
			action = 'select';
		}

		triggerGTMEvent( {
			event,
			action,
			group: pluginOrGroup.name,
			view: featuresView
		} );

		// Indeterminate → unchecked → checked. See: #427.
		if ( pluginOrGroup.isSelected || isSelectedIndeterminate ) {
			return onEditorPluginsDeselect( groupPlugins );
		}

		return onEditorPluginsSelect( groupPlugins );
	}

	const pluginSelectionWrapperStyles = {
		marginTop: '12px',
		paddingRight: '12px',
		display: 'flex',
		flexFlow: 'column',
		gap: '12px',
		overflowY: 'auto',
		scrollbarWidth: 'thin'
	};

	return (
		<SearchContext.Provider value={ searchQuery }>
			<Box display={ 'flex' } alignItems={ 'center' } justifyContent={ 'center' } padding={ '1px' }>
				<BuilderToggleButtons options={ views } selectedOption={ featuresView } onOptionChange={ onFeaturesViewChange }/>
			</Box>
			<Box>
				<SidebarSectionHeader
					title={ activeTab.tabName }
					description={ activeTab.description }
				/>
			</Box>
			<Box sx={ {
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: '12px',
				flex: 1,
				paddingTop: '12px'
			} }
			>
				<Box width='100%'>
					<SearchField onSearch={ setSearchQuery } placeholder='e.g. tables, lists' label='Search for features'/>
				</Box>
			</Box>
			<Box sx={ pluginSelectionWrapperStyles }>
				<NoResultsWrapper shouldShowResults={ hasVisibleCapabilities }>
					<ViewComponent
						filteredCapabilities={ filteredCapabilities }
						onPluginSelectionChange={ handleInputClick }
						onGroupToggle={ onGroupToggle }
						toolbarOptions={ toolbarOptions }
					/>
				</NoResultsWrapper>
			</Box>
		</SearchContext.Provider>
	);
}

function getPluginsToModifyFromGroup( pluginOrGroup: PluginOrPluginGroupParsed ): EditorPlugins {
	if ( isPlugin( pluginOrGroup ) ) {
		return [ pluginOrGroup.className ];
	}

	const isSingleChoiceCapability = isCapabilitySingleChoice( pluginOrGroup );

	// (*) When group is fully selected and is single choice, we want to deselect even the features which are filtered out by search.
	if ( pluginOrGroup.isSelected && isSingleChoiceCapability ) {
		return pluginOrGroup.plugins.map( p => p.className );
	}

	// (*) In other cases of fully selected group, we want to only modify the visible (non-filtered out) features.
	if ( pluginOrGroup.isSelected ) {
		return pluginOrGroup.plugins.filter( isPluginOrGroupAvailable ).map( p => p.className );
	}

	// (*) When processing a capability that allow choosing a single feature, find the default plugins.
	// If the default feature is filtered out or disabled, pick the first non-filtered out one instead.
	if ( isSingleChoiceCapability ) {
		const unfilteredFeatures = pluginOrGroup.features.filter( isPluginOrGroupAvailable );
		const featureToCheck = unfilteredFeatures.find( f => f.name === pluginOrGroup.defaultFeature ) || unfilteredFeatures[ 0 ];

		return featureToCheck.plugins.map( p => p.className );
	}

	// (*) When processing a capability that allows selecting many features, return all available unfiltered plugins.
	if ( isCapability( pluginOrGroup ) ) {
		return pluginOrGroup.features.filter( isPluginOrGroupAvailable ).flatMap( getPluginsFromFeature );
	}

	// (*) When processing a feature, find all of its plugins.
	return getPluginsFromFeature( pluginOrGroup );
}

function getPluginsFromFeature( feature: FeatureParsed ): EditorPlugins {
	const unfilteredPlugins = feature.plugins.filter( isPluginOrGroupAvailable );

	if ( !feature.isSinglePluginChoice ) {
		return unfilteredPlugins.map( p => p.className );
	}

	const { className } = feature.isSelected ?
		unfilteredPlugins.find( p => p.isSelected )! :
		unfilteredPlugins.find( p => p.className === feature.defaultPlugin ) || unfilteredPlugins[ 0 ];

	return [ className ];
}
